import {
  type DictationReportGuard,
  dictationReportGuardSchema,
} from "@rosc/contracts";

// ROSC Desktop (radiology push-to-talk) holds a report back when its value
// check fails and offers "Open in ROSC". The desktop keeps that check result
// in memory in this window (never in the URL or browser storage), and the
// Dictated report screen takes it once when it opens that report, so the
// lines behind the failed check are marked like after a fill here.

type DesktopBridge = {
  takeDictationGuard?: (visitId: string) => unknown;
};

/** The value check ROSC Desktop held this report for, once; else null. */
export const takeDesktopDictationGuard = (
  visitId: string,
): DictationReportGuard | null => {
  const bridge = (window as { roscDesktop?: DesktopBridge }).roscDesktop;
  if (typeof bridge?.takeDictationGuard !== "function") return null;
  try {
    const parsed = dictationReportGuardSchema.safeParse(
      bridge.takeDictationGuard(visitId),
    );
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
};

/** A saved report's screen state, with the check ROSC Desktop held it for. */
export const withDesktopDictationGuard = <S extends { guard: unknown }>(
  state: S,
  visitId: string,
): S => {
  const guard = takeDesktopDictationGuard(visitId);
  return guard ? { ...state, guard } : state;
};
